import type {
  AskBibleRequest,
  AskBibleResponse,
  BibleBook,
  BibleLocation,
  ChapterData,
  GraphData,
  GraphEdge,
  GraphNode,
  MapLocationsResponse,
  SearchResponse,
  SearchResult,
  TimelineData,
  TimelineEra,
  TimelineEvent,
  UniverseBookCluster,
  UniverseData,
  UniversePerson,
  UniversePosition,
  VerseReference,
} from "./types";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeTestament(value: unknown): "old" | "new" {
  const text = asString(value).toLowerCase();
  return text === "ot" || text === "old" ? "old" : "new";
}

function parseVerseReference(value: unknown): VerseReference | null {
  if (typeof value === "string") {
    const match = value.match(/^(.+?)\s+(\d+):(\d+)/);

    if (!match) {
      return null;
    }

    return {
      book: match[1],
      chapter: Number(match[2]),
      verse: Number(match[3]),
      text: "",
    };
  }

  const row = asRecord(value);
  const book = asString(row.book);

  if (!book) {
    return null;
  }

  return {
    book,
    chapter: asNumber(row.chapter),
    verse: asNumber(row.verse),
    text: asString(row.text),
    translation: asString(row.translation) || undefined,
  };
}

async function fetchJSON<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `API error ${response.status}: ${detail || response.statusText}`,
    );
  }

  return response.json() as Promise<T>;
}

// Chat helper for callers that do not use the Zustand streaming store.
export async function askBible(
  request: AskBibleRequest,
): Promise<AskBibleResponse> {
  const response = await fetch(`${API_BASE}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: request.question,
      translation: request.translation?.toUpperCase(),
    }),
  });

  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.status}`);
  }

  const body = await response.text();
  let answer = "";
  let verses: VerseReference[] = [];

  for (const line of body.split(/\r?\n/)) {
    if (!line.startsWith("data:")) {
      continue;
    }

    const content = line.slice(5).trim();

    if (!content) {
      continue;
    }

    try {
      const event = asRecord(JSON.parse(content));
      const type = asString(event.type);

      if (type === "token") {
        answer += asString(event.data);
      }

      if (type === "sources") {
        verses = asArray(event.data)
          .map(parseVerseReference)
          .filter((verse): verse is VerseReference => verse !== null);
      }
    } catch {
      // Ignore malformed SSE lines.
    }
  }

  return { answer, verses };
}

export async function searchBible(
  query: string,
  translation?: string,
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query });

  if (translation) {
    params.set("translation", translation.toUpperCase());
  }

  const raw = asRecord(
    await fetchJSON<unknown>(`/search?${params.toString()}`),
  );

  const results: SearchResult[] = asArray(raw.results).map((item) => {
    const result = asRecord(item);

    // The backend wraps each verse inside result.verse.
    const verse = asRecord(result.verse ?? result);

    return {
      book: asString(verse.book),
      chapter: asNumber(verse.chapter),
      verse: asNumber(verse.verse),
      text: asString(verse.text),
      translation: asString(verse.translation) || undefined,
      score: asNumber(result.score ?? verse.score),
    };
  });

  return {
    results,
    query: asString(raw.query, query),
    total: asNumber(raw.total, results.length),
  };
}

export async function getBooks(): Promise<BibleBook[]> {
  const raw = await fetchJSON<unknown>("/books");

  return asArray(raw).map((item) => {
    const row = asRecord(item);

    return {
      name: asString(row.name),
      chapters: asNumber(row.chapters),
      testament: normalizeTestament(row.testament),
    };
  });
}

export async function getChapter(
  book: string,
  chapter: number,
): Promise<ChapterData> {
  const raw = asRecord(
    await fetchJSON<unknown>(
      `/books/${encodeURIComponent(book)}/chapters/${chapter}`,
    ),
  );

  const verses = asArray(raw.verses)
    .map(parseVerseReference)
    .filter((verse): verse is VerseReference => verse !== null);

  return {
    book: asString(raw.book, book),
    chapter: asNumber(raw.chapter, chapter),
    verses,
  };
}

function normalizeGraph(rawValue: unknown): GraphData {
  const raw = asRecord(rawValue);

  const nodes: GraphNode[] = asArray(raw.nodes).map((item) => {
    const row = asRecord(item);

    return {
      id: asString(row.id),
      label: asString(row.label, asString(row.id)),
      type: asString(row.type, "event") as GraphNode["type"],
      x: asNumber(row.x),
      y: asNumber(row.y),
      size: asNumber(row.size, 8),
      metadata: asRecord(row.metadata),
      connections: asArray(row.connections)
        .map((value) => asString(value))
        .filter(Boolean),
    };
  });

  const edges: GraphEdge[] = asArray(raw.edges)
    .map((item, index) => {
      const row = asRecord(item);
      const source = asString(row.source);
      const target = asString(row.target);

      return {
        id: asString(row.id, `edge-${index}-${source}-${target}`),
        source,
        target,
        label: asString(row.label, asString(row.relationship)),
        weight: asNumber(row.weight, 1),
      };
    })
    .filter((edge) => edge.source && edge.target);

  return { nodes, edges };
}

export async function getGraph(): Promise<GraphData> {
  return normalizeGraph(await fetchJSON<unknown>("/graph"));
}

export async function searchGraphNodes(
  query: string,
): Promise<GraphData> {
  // Search locally so the result always has complete nodes and edges.
  const graph = await getGraph();
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return graph;
  }

  const directMatches = new Set(
    graph.nodes
      .filter((node) =>
        node.label.toLowerCase().includes(normalizedQuery),
      )
      .map((node) => node.id),
  );

  const included = new Set(directMatches);

  for (const edge of graph.edges) {
    if (directMatches.has(edge.source) || directMatches.has(edge.target)) {
      included.add(edge.source);
      included.add(edge.target);
    }
  }

  return {
    nodes: graph.nodes.filter((node) => included.has(node.id)),
    edges: graph.edges.filter(
      (edge) => included.has(edge.source) && included.has(edge.target),
    ),
  };
}

export async function getMapLocations(): Promise<MapLocationsResponse> {
  const raw = await fetchJSON<unknown>("/map/locations");
  const root = asRecord(raw);
  const source = Array.isArray(raw) ? raw : asArray(root.locations);

  const locations: BibleLocation[] = source.map((item) => {
    const row = asRecord(item);

    return {
      id: asString(row.id),
      name: asString(row.name),
      lat: asNumber(row.lat ?? row.latitude),
      lng: asNumber(row.lng ?? row.longitude),
      description: asString(row.description) || undefined,
      significance: asNumber(row.significance, 3),
      events: asArray(row.events)
        .map((event) => asString(event))
        .filter(Boolean),
      people: asArray(row.people)
        .map((person) => asString(person))
        .filter(Boolean),
      verses: asArray(row.verses)
        .map(parseVerseReference)
        .filter((verse): verse is VerseReference => verse !== null),
    };
  });

  return { locations };
}

export async function getTimeline(): Promise<TimelineData> {
  const raw = await fetchJSON<unknown>("/timeline");
  const root = asRecord(raw);
  const source = Array.isArray(raw) ? raw : asArray(root.events);

  const events: TimelineEvent[] = source.map((item) => {
    const row = asRecord(item);
    const locations = asArray(row.locations)
      .map((location) => asString(location))
      .filter(Boolean);

    return {
      id: asString(row.id),
      title: asString(row.title),
      description: asString(row.description),
      approxYear: asNumber(row.approxYear ?? row.approx_year),
      era: asString(row.era, "Other"),
      people: asArray(row.people)
        .map((person) => asString(person))
        .filter(Boolean),
      location: asString(row.location, locations[0] ?? "") || undefined,
      verses: asArray(row.verses)
        .map(parseVerseReference)
        .filter((verse): verse is VerseReference => verse !== null),
    };
  });

  const suppliedEras = asArray(root.eras);

  let eras: TimelineEra[];

  if (suppliedEras.length > 0) {
    eras = suppliedEras.map((item, index) => {
      const row = asRecord(item);

      return {
        id: asString(row.id, `era-${index}`),
        name: asString(row.name),
        startYear: asNumber(row.startYear ?? row.start_year),
        endYear: asNumber(row.endYear ?? row.end_year),
        color: asString(row.color, "#c8a45e"),
      };
    });
  } else {
    const order: string[] = [];
    const bounds = new Map<string, { min: number; max: number }>();

    for (const event of events) {
      if (!bounds.has(event.era)) {
        order.push(event.era);
        bounds.set(event.era, {
          min: event.approxYear,
          max: event.approxYear,
        });
      } else {
        const current = bounds.get(event.era)!;
        current.min = Math.min(current.min, event.approxYear);
        current.max = Math.max(current.max, event.approxYear);
      }
    }

    const palette = [
      "#6b21a8",
      "#d97706",
      "#dc2626",
      "#ea580c",
      "#78350f",
      "#c8a45e",
      "#7c6f4e",
      "#4b5563",
      "#0d9488",
      "#7ba4d4",
      "#5ea77b",
    ];

    eras = order.map((name, index) => {
      const range = bounds.get(name)!;

      return {
        id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        name,
        startYear: range.min,
        endYear: range.max,
        color: palette[index % palette.length],
      };
    });
  }

  return { eras, events };
}

const OT_BOOKS = new Set([
  "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy",
  "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel",
  "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles",
  "Ezra", "Nehemiah", "Esther", "Job", "Psalms", "Proverbs",
  "Ecclesiastes", "Song of Solomon", "Isaiah", "Jeremiah",
  "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel",
  "Amos", "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk",
  "Zephaniah", "Haggai", "Zechariah", "Malachi",
]);

function deriveUniverseClusters(
  positions: UniversePosition[],
): UniverseBookCluster[] {
  const grouped = new Map<string, UniversePosition[]>();

  for (const position of positions) {
    const existing = grouped.get(position.book) ?? [];
    existing.push(position);
    grouped.set(position.book, existing);
  }

  return Array.from(grouped.entries()).map(([book, verses]) => {
    const count = verses.length;
    const center: [number, number, number] = [
      verses.reduce((sum, value) => sum + value.x, 0) / count,
      verses.reduce((sum, value) => sum + value.y, 0) / count,
      verses.reduce((sum, value) => sum + value.z, 0) / count,
    ];

    const radius = Math.max(
      5,
      ...verses.map((value) =>
        Math.sqrt(
          (value.x - center[0]) ** 2 +
          (value.y - center[1]) ** 2 +
          (value.z - center[2]) ** 2,
        ),
      ),
    );

    return {
      book,
      testament: OT_BOOKS.has(book) ? "old" : "new",
      center,
      radius,
      verseCount: count,
    };
  });
}

export async function getUniverseData(): Promise<UniverseData> {
  const raw = await fetchJSON<unknown>("/universe");
  const root = asRecord(raw);
  const source = Array.isArray(raw) ? raw : asArray(root.positions);

  const positions: UniversePosition[] = source.map((item) => {
    const row = asRecord(item);

    return {
      id: asString(row.id, asString(row.verse_id)),
      book: asString(row.book),
      chapter: asNumber(row.chapter),
      verse: asNumber(row.verse),
      x: asNumber(row.x),
      y: asNumber(row.y),
      z: asNumber(row.z),
      text: asString(row.text) || undefined,
    };
  });

  const rawClusters = asArray(root.clusters);

  const clusters: UniverseBookCluster[] =
    rawClusters.length > 0
      ? rawClusters.map((item) => {
          const row = asRecord(item);
          const center = asArray(row.center);

          return {
            book: asString(row.book),
            testament: normalizeTestament(row.testament),
            center: [
              asNumber(center[0]),
              asNumber(center[1]),
              asNumber(center[2]),
            ],
            radius: asNumber(row.radius, 5),
            verseCount: asNumber(
              row.verseCount ?? row.verse_count,
            ),
          };
        })
      : deriveUniverseClusters(positions);

  const people: UniversePerson[] = asArray(root.people).map((item) => {
    const row = asRecord(item);
    const position = asArray(row.position);

    return {
      name: asString(row.name),
      position: [
        asNumber(position[0]),
        asNumber(position[1]),
        asNumber(position[2]),
      ],
      verseCount: asNumber(row.verseCount ?? row.verse_count),
      keyVerses: asArray(row.keyVerses ?? row.key_verses)
        .map(parseVerseReference)
        .filter((verse): verse is VerseReference => verse !== null),
    };
  });

  return {
    positions,
    clusters,
    people,
    constellationLines: Array.isArray(root.constellationLines)
      ? (root.constellationLines as UniverseData["constellationLines"])
      : [],
  };
}
